import { Action, ActionReducer, ActionReducerMap, combineReducers, MetaReducer } from '@ngrx/store';
import {
    createFormGroupState,
    createFormStateReducerWithUpdate,
    formGroupReducer,
    FormGroupState,
    setValue,
    updateGroup,
    validate
} from 'ngrx-forms';
import { GatewayActions } from '../../../../../core/state/gateway.actions';
import { ActionType, isAction } from '../../../../../shared/state/index.state';
import { MediaDevice } from '../../models/user-device.model';
import { UserDevicesActions } from './user-devices.actions';
import { AdminUserDevicesFormValue, AdminUserDevicesState } from './user-devices.state';

export const FORM_ID = 'userDevicesForm contained in AdminUserDevicesState';

const initialDevicesValue = {
    smsDevice: {
        deviceId: '',
        isChecked: false,
        userSenderAddress: '',
        userSenderId: 0,
        type: 'sms',
        isSharedNumber: false
    },

    emailDevice: {
        deviceId: '',
        isChecked: false,
        type: 'email'
    },

    voiceDevice: {
        deviceId: '',
        isChecked: false,
        type: 'voice'
    }
    // devices: [{ // initial test - not the route I want to take
    //    isChecked: false,
    //    deviceId: '',
    //    userSenderAddress: '',
    //    userSenderId: 0,
    //    type: 'SMS'
    // } // initialSMSMediaDevice
    // ]
};

export const INITIAL_FORM_STATE: FormGroupState<AdminUserDevicesFormValue> =
    createFormGroupState<AdminUserDevicesFormValue>(FORM_ID, initialDevicesValue);

interface ValidationErrors {
    message?: string;
}

const phoneNumberValidator = (value: string): ValidationErrors => {
    if (value.length === 0) {
        return {};
    }

    // Remove characters often used in phone numbers (spaces, -, +, #) to allow copy'n'paste

    if (/^([0-9]{1,25})$/.test(value)) {
        return {};
    }

    // Invalid
    return { // return custom object with errors to display message on UI
        message: 'Phone number incorrect format'
        // This will appear in userDevicesForm.formState.errors._smsDevice._userSenderAddress.message in the updated state
        // This change of state will NOT be displayed in Diff in the Redux DevTools
    };
};

/**
 * type: <FormGroupState<AdminUserDevicesFormValue> => FormGroupState<AdminUserDevicesFormValue>>
 */
const validateAndUpdateUserDevicesFormState = updateGroup<AdminUserDevicesFormValue>(
    {
        // this will update isValid/isInvalid in state
        smsDevice: updateGroup<AdminUserDevicesFormValue['smsDevice']>({
            // If form is valid, userDevicesForm.isValid = true
            userSenderAddress: validate(phoneNumberValidator)
        })
    }
);

/**
 * https://ngrx-forms.readthedocs.io/en/master/user-guide/updating-the-state/#createformstatereducerwithupdate
 * type: {ActionReducer<FormState<AdminUserDevicesFormValue>>}
 */
const validateAndUpdateFormReducer = createFormStateReducerWithUpdate<AdminUserDevicesFormValue>(validateAndUpdateUserDevicesFormState);

const userDevicesFormStateReducer = (state = INITIAL_FORM_STATE, action: Action): FormGroupState<AdminUserDevicesFormValue> => {
    // Handle validation (regardless of which actions trigger the reducer - since this might change state based on valiation
    const validatedState = validateAndUpdateFormReducer(state, action) as FormGroupState<AdminUserDevicesFormValue>;
    if (validatedState !== state) {
        console.log('Form state changed due to validator(s): ',
            (validatedState.errors._smsDevice !== undefined ? validatedState.errors._smsDevice._userSenderAddress.message : 'valid'));
        // state = {...validatedState}; // No need to create duplicate since validatedState is already a duplicate if updated
    }
    // NOTE: This seems to ALWAYS update the state even when valid

    // First handle API actions - before form actions (that are handled by formGroupReducer)

    if (isAction(action as ActionType<any>, UserDevicesActions.GET_USER_DEVICES_SUCCESS)) {
        const devices: Array<MediaDevice> = (action as ActionType<any>).payload;

        const formValue: AdminUserDevicesFormValue = {
            // Transform array of devices into flat structure for the form
            smsDevice: (devices as Array<MediaDevice>)
                .filter(device => device.deviceId.indexOf('sms') > -1)[0] || initialDevicesValue.smsDevice,
            emailDevice: devices
                .filter(device => device.deviceId.indexOf('email') > -1)[0] || initialDevicesValue.emailDevice,
            voiceDevice: devices
                .filter(device => device.deviceId.indexOf('voice') > -1)[0] || initialDevicesValue.voiceDevice
        };

        return setValue(formValue)(validatedState);
    }

    if (isAction(action as ActionType<any>, UserDevicesActions.SAVE_USER_DEVICE_FAIL)) {
        // This will change state to isUnsubmitted=true right at the time we get the error from the backend
        // return markAsUnsubmitted(state);
        // This has same effect - and dispite the name it does not "emit" the action to be seen in the Redux Dev Tools
        // return formStateReducer(state, new MarkAsUnsubmittedAction(FORM_ID));
        // In both cases above, the change will be displayed as part of the SAVE_USER_DEVICE_FAIL action change
    }

    // Then handle form change states

    return formGroupReducer(validatedState, action);
};

const loadingReducer = (state = false, action: Action): boolean => {

    if (isAction(action as ActionType<any>,
        UserDevicesActions.GET_USER_DEVICES,
        UserDevicesActions.SAVE_USER_DEVICES,
        GatewayActions.GET_SHARED_NUMBERS)) {
        return true;
    }

    if (isAction(action as ActionType<any>,
        UserDevicesActions.GET_USER_DEVICES_SUCCESS,
        UserDevicesActions.GET_USER_DEVICES_FAIL,
        UserDevicesActions.SAVE_USER_DEVICE_SUCCESS,
        UserDevicesActions.SAVE_USER_DEVICE_FAIL,
        GatewayActions.GET_SHARED_NUMBERS_SUCCESS,
        GatewayActions.GET_SHARED_NUMBERS_FAIL)) {
        return false;
    }

    return state;
};

/**
 * All combineReducers() does is generate a function that calls your reducers with the slices of state selected
 * according to their keys, and combines their results into a single object again. It's not magic.
 * And like other reducers, combineReducers() does not create a new object if all of the reducers
 * provided to it do not change state.
 */
const adminUserDevicesStateMetaReducer: ActionReducer<AdminUserDevicesState['userDevicesForm'], any> =
    combineReducers<AdminUserDevicesState['userDevicesForm'], any>({
        formState: userDevicesFormStateReducer,
        loading: loadingReducer
    });

/**
 * Due to AOT constraints we can't call functions like combineReducers() in StoreModule.forFeature(...) when
 * defining metadata. This compiler to statically analyse the code.
 * In stead we create the meta reducer once while the exported reducer is called for every action.
 * https://github.com/ngrx/platform/blob/master/docs/store/api.md#feature-module-reducers-and-aot
 */
// tslint:disable-next-line:only-arrow-functions (AOT required named function - not arrow notation)
export function adminUserDevicesStateReducers(state, action): any {
    return adminUserDevicesStateMetaReducer(state, action);
}

export const adminUserDevicesStateMetaReducer2: ActionReducerMap<any> = {
    formState: userDevicesFormStateReducer,
    loading: loadingReducer
};
